import { useEffect, useCallback, useState, RefObject } from 'react';
import useFocusTrap, { FOCUSABLE_ELEMENTS_SELECTOR } from '../useFocusTrap';

interface UseModalFocusProps {
    open?: boolean;
    componentRef: RefObject<HTMLDivElement>;
    anchorElement?: HTMLElement | null;
    defaultFocusElement?: HTMLElement | null | (() => HTMLElement | null);
}

export const useModalFocus = ({ open, componentRef, anchorElement, defaultFocusElement }: UseModalFocusProps) => {
    const [componentElement, setComponentElement] = useState<HTMLDivElement | null>(null);

    const getDefaultFocusElement = useCallback((): HTMLElement | null => {
        if (typeof defaultFocusElement === 'function') {
            return defaultFocusElement();
        }
        return defaultFocusElement || null;
    }, [defaultFocusElement]);

    const focusElement = useCallback((): void => {
        if (!componentRef.current) return;

        // If a default focus element is provided and it's focusable, use it
        const elementToFocus = getDefaultFocusElement();
        if (elementToFocus && elementToFocus.matches(FOCUSABLE_ELEMENTS_SELECTOR)) {
            elementToFocus.focus({ preventScroll: true });
            return;
        }

        const firstFocusable = componentRef.current.querySelector<HTMLElement>(FOCUSABLE_ELEMENTS_SELECTOR);
        if (firstFocusable) {
            // Prevent scroll into view during animation
            firstFocusable.focus({ preventScroll: true });
        } else {
            // Fallback: focus component itself
            componentRef.current.setAttribute('tabindex', '-1');
            componentRef.current.focus({ preventScroll: true });
        }
    }, [componentRef, getDefaultFocusElement]);

    // Handle initial focus when modal opens
    useEffect(() => {
        if (open && componentRef.current) {
            const focusRequest = window.requestAnimationFrame(() => {
                focusElement();
            });

            return () => window.cancelAnimationFrame(focusRequest);
        }
    }, [open, componentRef, focusElement]);

    // Track when component element becomes available for focus trapping
    useEffect(() => {
        if (open && componentRef.current && componentRef.current !== componentElement) {
            setComponentElement(componentRef.current);
        } else if (!open) {
            setComponentElement(null);
        }
    }, [open, componentRef, componentElement]);

    // Handle focus trapping within the component only when element is available
    useFocusTrap(open && componentElement ? componentElement : null);

    // Handle focus return to anchor element when modal closes
    useEffect(() => {
        if (!open && anchorElement) {
            const focusRequest = window.requestAnimationFrame(() => {
                // Prevent scroll into view during close animation
                anchorElement.focus({ preventScroll: true });
            });

            return () => window.cancelAnimationFrame(focusRequest);
        }
    }, [open, anchorElement]);

    // Remove tabindex if any was added when modal closes from the container element
    useEffect(() => {
        if (!open && componentRef.current && componentRef.current.hasAttribute('tabindex')) {
            componentRef.current.removeAttribute('tabindex');
        }
    }, [open, componentRef]);
};
